import type { DependsOnType } from '@unifyapps/defs/types/page';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import type {
  BuildDependencyGraphItem,
  BuildDependencyGraphParams,
} from '../../dependency/utils/buildDependencyGraph';
import { buildDependencyGraph } from '../../dependency/utils/buildDependencyGraph';
import type { DynamicAndConditionalPathHandler } from '../context/DependencyGraphProvider/handlers';
import { parseRuntimeBlockPathId } from '../utils/parseRuntimeBlockPathId';
import { isRuntimeBlockId } from '../utils/runTimeBlocks';

function noCodeBuildDependencyGraph(
  params: BuildDependencyGraphParams<DynamicAndConditionalPathHandler>,
) {
  const dependencies = buildDependencyGraph<DynamicAndConditionalPathHandler>(params);

  // Transform the dependencies to handle runtime block references
  return Object.entries(dependencies).reduce<
    Record<string, BuildDependencyGraphItem<DynamicAndConditionalPathHandler> | undefined>
  >((acc, [entityId, depGraph]) => {
    if (!depGraph) {
      return acc;
    }

    /**
     * dependsOn generated by buildDependencyGraph adds runtime block references as is. but we actually want to
     * know the original block ID that the runtime block reference points to. So we need to transform the dependsOn
     * array to use the original block ID instead of the runtime block reference.
     *
     * In the following code, we are adapting the id and property references
     */
    const newDependsOn = depGraph.dependsOn?.reduce<DependsOnType[]>((accDependsOn, dependsOn) => {
      const id = DependencyHelper.getDependsOnId(dependsOn);
      const properties = DependencyHelper.getDependsOnProperties(dependsOn);

      let newId = id;
      let newProperties = properties;

      if (isRuntimeBlockId(id)) {
        const parsedId = parseRuntimeBlockPathId(id);

        if (!parsedId) {
          // Not a runtime block reference, return as is
          accDependsOn.push(dependsOn);
          return accDependsOn;
        }

        newId = parsedId.targetBlockId;

        // transform paths to use the correct block ID format
        newProperties = properties.map((property) => {
          const parsedPath = parseRuntimeBlockPathId(property);
          if (!parsedPath) {
            return property; // not a runtime block path, return as is
          }

          // create the new path using the target block ID and any remaining path components
          return `${parsedPath.targetBlockId}${parsedPath.remainingPathString}`;
        });
      }

      accDependsOn.push({
        id: newId,
        p: newProperties,
      });

      return accDependsOn;
    }, []);

    // from all the dependsOn, we filter ourselves out
    // this is because we don't want to depend on ourselves
    const externalDependsOn = newDependsOn?.filter((dependsOn) => dependsOn.id !== entityId);

    acc[entityId] = {
      ...depGraph,
      dependsOn: externalDependsOn,
    };

    return acc;
  }, {});
}

export default noCodeBuildDependencyGraph;
